import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "../utils/analytics";

const Icon = ({ name }) => (
  <span className="material-icons" style={{
    fontSize: 16, verticalAlign: "middle", margin: "0 2px",
    background: "#1a1a1a", color: "#ffe600", borderRadius: 2,
    padding: "2px 3px", display: "inline-flex", alignItems: "center",
  }}>{name}</span>
);

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: "play_arrow",
    intro: "Striply is a browser-based comic page layout editor. You design pages by choosing a panel grid, uploading artwork, adding speech bubbles and sound effects, then exporting to PDF or PNG.",
    steps: [
      <>Open the <Link to="/editor" style={{ color: "#00a4e4" }}>Editor</Link> to begin. A random layout template is loaded automatically.</>,
      <>Use the <Icon name="dashboard" /> <strong>Layout</strong> dropdown in the toolbar to pick a different grid (2×2, 3×3, manga-style, etc.) or choose <em>Custom…</em> to set your own rows and columns.</>,
      <>Choose a <strong>Page size</strong> — Letter, A4, or manga B5 — from the dropdown beside the layout selector.</>,
      <>Click any panel on the canvas to select it. The toolbar's second row will show controls specific to that panel.</>,
    ],
    tip: "You can import a previously saved layout via the JSON import button to pick up right where you left off.",
  },
  {
    id: "panels",
    title: "Working with Panels",
    icon: "dashboard",
    intro: "Panels are the cells of your comic page grid. You can resize them, change their shape, adjust borders, and control stacking order.",
    steps: [
      <>Click a panel to select it. The selection label in the toolbar shows which cell you're editing (e.g. "R1C1").</>,
      <>Drag any edge or corner of a panel to resize it. The adjacent panels will adjust automatically.</>,
      <>Use the <strong>Panel ▾</strong> dropdown to change the panel shape (rectangle, circle, diamond, etc.), adjust border width/style/color, or change z-order.</>,
      <>To merge panels, hold <strong>Shift</strong> and click multiple panels, then click <Icon name="call_merge" /> <strong>Merge</strong>. Use <strong>Unmerge</strong> in the Panel dropdown to split them apart.</>,
    ],
    tip: "Right-click a panel or use the Panel dropdown's Z controls to bring panels forward or send them backward.",
  },
  {
    id: "backgrounds",
    title: "Backgrounds & Images",
    icon: "add_photo_alternate",
    intro: "Each panel can hold a background image. Upload artwork and use zoom/pan to frame it perfectly within the panel.",
    steps: [
      <>Select a panel, then click the <Icon name="add_photo_alternate" /> button to upload an image.</>,
      <>Use the <strong>scroll wheel</strong> over a panel to zoom in/out on the background image.</>,
      <>Click and drag the background image to pan it within the panel.</>,
      <>Open the <strong>Background ▾</strong> dropdown for a zoom slider and a reset button to restore the default view.</>,
    ],
    tip: "Click the layers-clear button to remove the topmost item from a panel (background, bubble, or SFX).",
  },
  {
    id: "bubbles",
    title: "Speech Bubbles",
    icon: "mode_comment",
    intro: "Speech bubbles let you add dialogue and narration to your panels. Each bubble is fully customizable in shape, style, and text.",
    steps: [
      <>Select a panel and click <Icon name="mode_comment" /> to add a speech bubble.</>,
      <>Choose a bubble shape — <strong>rounded</strong>, <strong>square</strong>, <strong>cloud</strong> (thought), <strong>burst</strong> (shout), or <strong>whisper</strong> (dashed) — from the shape buttons in the toolbar.</>,
      <>Drag a bubble to reposition it within (or outside) its panel.</>,
      <>Drag the corner handles to resize the bubble. Double-click to edit the text directly.</>,
    ],
    tip: "Hold Alt and click a bubble to instantly duplicate it, or use Ctrl+D.",
  },
  {
    id: "tails",
    title: "Bubble Tails",
    icon: "call_made",
    intro: "Tails are the pointers that connect a speech bubble to its speaker. Striply supports multiple tails per bubble and several visual styles.",
    steps: [
      <>Select a bubble, then open the <strong>Tail ▾</strong> dropdown. Toggle the tail on or off.</>,
      <>Drag the tail tip to point at the character who's speaking.</>,
      <>Choose a tail style: <strong>pointed</strong>, <strong>curved</strong>, <strong>jagged</strong>, <strong>thought</strong> (circles), or <strong>right-angle</strong>.</>,
      <>Click <Icon name="add" /> in the Tail dropdown to add a second or third tail (for multiple speakers). Click <Icon name="remove" /> to remove the last tail.</>,
    ],
    tip: "Adjust the tail width slider for thicker or thinner tails. Use the reset button to snap the tail back to its default position.",
  },
  {
    id: "text-styling",
    title: "Text & Lettering",
    icon: "text_format",
    intro: "Fine-tune your dialogue text with font choices, sizing, and alignment — all from the Text dropdown.",
    steps: [
      <>Select a bubble and open the <strong>Text ▾</strong> dropdown.</>,
      <>Choose a <strong>font family</strong> from the list of comic-friendly fonts (Bangers, Comic Neue, Permanent Marker, etc.).</>,
      <>Set the <strong>font size</strong> with the number input. Typical comic dialogue is 12–16px.</>,
      <>Toggle <strong>Bold</strong>, <strong>Italic</strong>, or <strong>ALL CAPS</strong> with the B / I / AA buttons. Set text alignment to left, center, or right.</>,
    ],
    tip: "ALL CAPS is the traditional lettering style for Western comics. Turn it on for an authentic look.",
  },
  {
    id: "sfx",
    title: "Sound Effects (SFX)",
    icon: "text_fields",
    intro: "SFX text items are bold, stylized words — like WHAM!, CRASH!, or WHOOSH — placed directly on your panels for dramatic effect.",
    steps: [
      <>Select a panel and click <Icon name="text_fields" /> to add an SFX item. A default "BAM!" appears.</>,
      <><strong>Double-click</strong> the SFX text on the canvas to edit it inline.</>,
      <>Drag the SFX to reposition it. Use the resize handle to scale the font size.</>,
      <>Open the <strong>Style ▾</strong> dropdown to change the font, fill color, stroke color, and rotation angle.</>,
    ],
    tip: "SFX text uses a separate rendering system from bubbles — it supports independent fill and stroke colors for that classic comic-book pop.",
  },
  {
    id: "scaling",
    title: "SFX Scaling",
    icon: "aspect_ratio",
    intro: "Beyond simple font sizing, SFX items support independent horizontal and vertical scaling for dynamic, stretched lettering.",
    steps: [
      <>Select an SFX item and open the <strong>Scale ▾</strong> dropdown.</>,
      <>Drag the <strong>W</strong> (width) slider to stretch or compress horizontally.</>,
      <>Drag the <strong>H</strong> (height) slider to stretch or compress vertically.</>,
      <>Click <Icon name="restart_alt" /> to reset both scales back to 100%.</>,
    ],
    tip: "Hold Shift while dragging a 2D resize handle on the canvas to scale proportionally in both axes at once.",
  },
  {
    id: "colors",
    title: "Colors & Opacity",
    icon: "palette",
    intro: "Control the visual appearance of your speech bubbles with background, border, and text color pickers, plus an opacity slider.",
    steps: [
      <>Select a bubble and open the <strong>Colors ▾</strong> dropdown.</>,
      <>Use the <strong>BG</strong> color picker to change the bubble's background fill.</>,
      <>Use <strong>Bdr</strong> for the border color and <strong>Txt</strong> for the text color.</>,
      <>Drag the <strong>opacity slider</strong> to make the bubble semi-transparent (useful for narration boxes).</>,
    ],
    tip: "A fully transparent bubble with visible text makes a great floating narration caption.",
  },
  {
    id: "screentones",
    title: "Screentones",
    icon: "grain",
    intro: "Screentones add halftone dot patterns to panels — a classic technique in manga and black-and-white comics for shading and texture.",
    steps: [
      <>Select a panel and open the <strong>Panel ▾</strong> dropdown.</>,
      <>In the Screentone section, choose a pattern: <strong>dots</strong>, <strong>lines</strong>, <strong>crosshatch</strong>, or <strong>none</strong>.</>,
      <>Adjust the <strong>opacity slider</strong> to control how subtle or prominent the pattern appears.</>,
    ],
    tip: "Screentones are applied as an overlay, so they work on top of background images too.",
  },
  {
    id: "snapping",
    title: "Snap & Alignment",
    icon: "grid_3x3",
    intro: "Snap-to-grid and alignment guides help you position bubbles and SFX precisely on the canvas.",
    steps: [
      <>Click the <Icon name="grid_3x3" /> button in the toolbar to toggle snapping on or off.</>,
      <>Click the <Icon name="grid_4x4" /> button to show or hide the grid overlay on the canvas.</>,
      <>When snapping is enabled, dragging a bubble or SFX will snap to grid lines. Alignment guides (cyan lines) appear when items align with each other.</>,
      <>Hold <strong>Alt</strong> while dragging to temporarily bypass snapping.</>,
    ],
    tip: "The grid size is derived from your gutter width, so adjusting the gutter slider changes the snap spacing too.",
  },
  {
    id: "locking",
    title: "Lock & Unlock",
    icon: "lock",
    intro: "Lock bubbles or SFX in place to prevent accidental moves while you work on other elements.",
    steps: [
      <>Select a bubble or SFX item and click the <Icon name="lock_open" /> button to lock it.</>,
      <>A locked item shows an <strong>amber badge</strong> on the canvas. It cannot be dragged or resized.</>,
      <>You can still <strong>select</strong> a locked item, <strong>edit its text</strong>, and change its properties in the toolbar.</>,
      <>Click the <Icon name="lock" /> button again to unlock the item and restore full interactivity.</>,
    ],
    tip: "Lock your finalized dialogue bubbles before repositioning background images so you don't accidentally nudge them.",
  },
  {
    id: "layer-ordering",
    title: "Layer Ordering",
    icon: "swap_vert",
    intro: "When speech bubbles and SFX overlap inside a panel, you can control which items appear in front of others.",
    steps: [
      <>Select a bubble or SFX item. The toolbar shows a <strong>Layer:</strong> section with four buttons.</>,
      <>Click <Icon name="vertical_align_top" /> to <strong>bring to front</strong>, or <Icon name="arrow_upward" /> to <strong>bring forward</strong> one step.</>,
      <>Click <Icon name="arrow_downward" /> to <strong>send backward</strong> one step, or <Icon name="vertical_align_bottom" /> to <strong>send to back</strong>.</>,
      <>You can also right-click a selected item to access layer ordering from the context menu.</>,
    ],
    tip: "Background images always stay behind all bubbles and SFX. Use Cmd+] / Cmd+[ (Ctrl on Windows) to reorder layers with the keyboard.",
  },
  {
    id: "export",
    title: "Export & Save",
    icon: "download",
    intro: "When your page is ready, export it as a high-resolution PDF or PNG, or save your work as JSON for later.",
    steps: [
      <>Choose <strong>PDF</strong> or <strong>PNG</strong> from the format dropdown in the toolbar.</>,
      <>Select a <strong>DPI</strong> setting — 150 for screen, 300 for print, 600 for high-quality print.</>,
      <>Click <Icon name="download" /> to download your file.</>,
      <>Use <Icon name="upload_file" /> <strong>Export JSON</strong> to save your layout as a file. Use <Icon name="file_open" /> <strong>Import JSON</strong> to reload it later.</>,
    ],
    tip: "Your layout is auto-saved in the browser. Exporting JSON creates a portable backup you can share or archive.",
  },
  {
    id: "storyboards",
    title: "Storyboards",
    icon: "movie",
    intro: <>Planning a film or video? The <Link to="/storyboard" style={{ color: "#00a4e4" }}>storyboard maker</Link> uses the same editor with frames locked to a camera aspect ratio and notes under every shot.</>,
    steps: [
      <>Open the <Link to="/storyboard/editor" style={{ color: "#00a4e4" }}>storyboard editor</Link>, or start from a <Link to="/storyboard-templates" style={{ color: "#00a4e4" }}>storyboard template</Link> (16:9, 2.39:1, 9:16 vertical, or animation).</>,
      <>Use <strong>Board</strong> to choose how many frames sit on a page and whether notes go under or beside them, and <strong>Frame</strong> to set the aspect ratio. Every frame follows.</>,
      <>Use <strong>Matte</strong> to add letterbox bars (2.39:1, 1.85:1) or a crop guide for another platform, such as a 9:16 vertical crop. Click <Icon name="crop_free" /> to show action-safe and title-safe areas.</>,
      <>Type straight into each frame's notes: pick the shot type and camera move in the header, set a duration, then describe the action and dialogue.</>,
      <>Add pages as the sequence grows — shot numbers continue across pages — and use <strong>Export storyboard (PDF)</strong> in the page list to print the whole thing.</>,
    ],
    tip: <>New to shot vocabulary? Read the <Link to="/guides/camera-shot-types" style={{ color: "#00a4e4" }}>guide to camera shot types</Link>.</>,
  },
  {
    id: "shortcuts",
    title: "Keyboard Shortcuts",
    icon: "keyboard",
    intro: "Speed up your workflow with these keyboard shortcuts.",
    steps: [
      <><strong>Ctrl+Z</strong> — Undo the last action. <strong>Ctrl+Shift+Z</strong> (or Ctrl+Y) — Redo.</>,
      <><strong>Ctrl+D</strong> — Duplicate the selected bubble or SFX.</>,
      <><strong>Alt+Click</strong> — Duplicate a bubble or SFX at the click position.</>,
      <><strong>Shift+Click</strong> — Multi-select panels for merging. <strong>Delete</strong> — Remove the selected item.</>,
      <><strong>Ctrl+]</strong> — Bring selected item forward. <strong>Ctrl+Shift+]</strong> — Bring to front.</>,
      <><strong>Ctrl+[</strong> — Send selected item backward. <strong>Ctrl+Shift+[</strong> — Send to back.</>,
    ],
    tip: "Most shortcuts work the same on Mac with Cmd instead of Ctrl.",
  },
  {
    id: "dark-mode",
    title: "Dark Mode",
    icon: "dark_mode",
    intro: "Toggle dark mode for comfortable editing in low-light environments.",
    steps: [
      <>Click the <Icon name="dark_mode" /> button in the toolbar to switch between light and dark mode.</>,
      <>Dark mode changes the toolbar, dropdowns, and canvas background to darker tones while keeping your export colors unchanged.</>,
      <>Your dark mode preference is saved automatically and persists across sessions.</>,
    ],
    tip: "Dark mode only affects the editor UI — your exported PDFs and PNGs always use the colors you've set.",
  },
];

const TutorialPage = () => {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const sectionRefs = useRef({});
  const viewedSections = useRef(new Set());

  useEffect(() => {
    trackEvent("tutorial_opened");
  }, []);

  useEffect(() => {
    const observers = [];
    const els = Object.values(sectionRefs.current).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            if (!viewedSections.current.has(entry.target.id)) {
              viewedSections.current.add(entry.target.id);
              trackEvent("tutorial_section_viewed", { section_id: entry.target.id });
            }
          }
        }
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );
    els.forEach((el) => { observer.observe(el); observers.push(el); });
    return () => observers.forEach((el) => observer.unobserve(el));
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
      }
    }
  }, []);

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <Link to="/" style={styles.logo}>striply</Link>
        <nav style={styles.nav}>
          <Link to="/guides" style={styles.navLink}>Guides</Link>
          <Link to="/editor" style={styles.navLink}>Editor</Link>
          <Link to="/about" style={styles.navLink}>About</Link>
        </nav>
      </header>

      <div style={styles.body}>
        {/* Sidebar TOC */}
        <nav className="tutorial-sidebar" style={styles.sidebar}>
          <h3 style={styles.sidebarTitle}>Contents</h3>
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              style={{
                ...styles.tocLink,
                ...(activeSection === s.id ? styles.tocLinkActive : {}),
              }}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span className="material-icons" style={{ fontSize: 14 }}>{s.icon}</span>
              {s.title}
            </a>
          ))}
        </nav>

        {/* Main content */}
        <main className="tutorial-main" style={styles.main}>
          <h1 style={styles.title}>Tutorial</h1>
          <p style={styles.subtitle}>
            Everything you need to know about building comic pages with Striply.
          </p>
          <div style={styles.guidesCallout}>
            Looking for the craft behind the tool — page layout, composition,
            lettering, and print prep? Read our{" "}
            <Link to="/guides" style={{ color: "#00a4e4", fontWeight: 700 }}>
              comic-making guides
            </Link>.
          </div>

          {SECTIONS.map((section) => (
            <section
              key={section.id}
              id={section.id}
              ref={(el) => { sectionRefs.current[section.id] = el; }}
              style={styles.card}
            >
              <div style={styles.cardHeader}>
                <span className="material-icons" style={styles.cardIcon}>{section.icon}</span>
                <h2 style={styles.cardTitle}>{section.title}</h2>
              </div>
              <div style={styles.cardBody}>
                <p style={styles.text}>{section.intro}</p>
                <div style={styles.stepsBox}>
                  <ol style={styles.stepsList}>
                    {section.steps.map((step, i) => (
                      <li key={i} style={styles.stepItem}>{step}</li>
                    ))}
                  </ol>
                </div>
                {section.tip && (
                  <div style={styles.tipBox}>
                    <strong>Pro tip:</strong> {section.tip}
                  </div>
                )}
              </div>
            </section>
          ))}

          <div style={styles.ctaSection}>
            <Link to="/editor" style={styles.ctaButton}>
              Open the Editor
            </Link>
          </div>
        </main>
      </div>

      <footer style={styles.footer}>
        <nav style={styles.footerNav}>
          <Link to="/" style={styles.footerLink}>Home</Link>
          <Link to="/guides" style={styles.footerLink}>Guides</Link>
          <Link to="/editor" style={styles.footerLink}>Editor</Link>
          <Link to="/about" style={styles.footerLink}>About</Link>
          <Link to="/privacy" style={styles.footerLink}>Privacy</Link>
          <Link to="/terms" style={styles.footerLink}>Terms</Link>
        </nav>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#1a1a1a",
    lineHeight: 1.7,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#f5f0e8",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 40px",
    borderBottom: "2px solid #1a1a1a",
  },
  logo: {
    fontSize: 28,
    fontWeight: 700,
    color: "#1a1a1a",
    textDecoration: "none",
    fontFamily: "'Georgia', serif",
  },
  nav: {
    display: "flex",
    gap: 20,
  },
  navLink: {
    color: "#555",
    textDecoration: "none",
    fontSize: 15,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  body: {
    flex: 1,
    display: "flex",
    maxWidth: 1100,
    margin: "0 auto",
    width: "100%",
  },
  sidebar: {
    position: "sticky",
    top: 0,
    alignSelf: "flex-start",
    width: 220,
    padding: "32px 16px 32px 24px",
    borderRight: "1px solid #d5cfc5",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flexShrink: 0,
  },
  sidebarTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#999",
    marginBottom: 12,
  },
  tocLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 8px",
    fontSize: 13,
    color: "#666",
    textDecoration: "none",
    borderRadius: 0,
    borderLeft: "2px solid transparent",
  },
  tocLinkActive: {
    color: "#1a1a1a",
    fontWeight: 700,
    borderLeft: "2px solid #00a4e4",
    background: "rgba(0, 164, 228, 0.06)",
  },
  main: {
    flex: 1,
    padding: "48px 24px 48px 40px",
    maxWidth: 780,
  },
  title: {
    fontSize: 42,
    fontWeight: 700,
    marginBottom: 8,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 17,
    color: "#666",
    marginBottom: 16,
  },
  guidesCallout: {
    fontSize: 15,
    color: "#444",
    background: "#fffbe6",
    border: "1px solid #e8dfae",
    padding: "12px 16px",
    marginBottom: 40,
    lineHeight: 1.6,
  },
  card: {
    marginBottom: 32,
    border: "2px solid #1a1a1a",
    background: "#fff",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    background: "#1a1a1a",
  },
  cardIcon: {
    fontSize: 20,
    color: "#ffe600",
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    margin: 0,
  },
  cardBody: {
    padding: "20px 20px 16px",
  },
  text: {
    fontSize: 15,
    marginBottom: 16,
    color: "#444",
  },
  stepsBox: {
    background: "#faf7f2",
    border: "1px solid #e2ddd5",
    padding: "12px 16px 12px 20px",
    marginBottom: 12,
  },
  stepsList: {
    margin: 0,
    paddingLeft: 18,
  },
  stepItem: {
    fontSize: 14,
    marginBottom: 8,
    color: "#333",
    lineHeight: 1.6,
  },
  tipBox: {
    background: "#e8f4fa",
    border: "1px solid #b8dce8",
    padding: "10px 14px",
    fontSize: 13,
    color: "#1a6b8a",
    lineHeight: 1.5,
  },
  ctaSection: {
    textAlign: "center",
    padding: "32px 0",
  },
  ctaButton: {
    display: "inline-block",
    padding: "14px 32px",
    background: "#1a1a1a",
    color: "#f5f0e8",
    fontSize: 16,
    fontWeight: 700,
    borderRadius: 0,
    textDecoration: "none",
    textTransform: "uppercase",
    letterSpacing: 1,
    border: "2px solid #1a1a1a",
  },
  footer: {
    padding: "24px 40px",
    borderTop: "2px solid #1a1a1a",
    background: "#f5f0e8",
  },
  footerNav: {
    display: "flex",
    justifyContent: "center",
    gap: 32,
  },
  footerLink: {
    color: "#666",
    textDecoration: "none",
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};

// Media query: hide sidebar on small screens
const sidebarMediaStyle = document.createElement("style");
sidebarMediaStyle.textContent = `
  @media (max-width: 900px) {
    .tutorial-sidebar { display: none !important; }
    .tutorial-main { padding-left: 24px !important; }
  }
`;
if (!document.querySelector("[data-tutorial-style]")) {
  sidebarMediaStyle.setAttribute("data-tutorial-style", "");
  document.head.appendChild(sidebarMediaStyle);
}

export default TutorialPage;
